import { useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { QuickAnswerBox } from "@/components/QuickAnswerBox";
import { COMPARISONS } from "@/data/comparisons";
import { useListings } from "@/hooks/use-listings";
import { Scale, CheckCircle2, ArrowRight, Table, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import NotFound from "./NotFound";

export default function ComparisonPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { listings, loading, error } = useListings();

  if (slug) {
    const item = COMPARISONS.find((c) => c.slug === slug);
    if (!item) return <><SEOHead title="Karşılaştırma bulunamadı" noindex /><NotFound /></>;
    const countForArea = (area: typeof item.areaA) => listings.filter((listing) => listing.ilceSlug === area.districtSlug && (!area.neighborhoodSlug || listing.mahalleSlug === area.neighborhoodSlug)).length;

    return (
      <div className="min-h-screen bg-background flex flex-col justify-between">
        <SEOHead
          title={`${item.title} | Corner Gayrimenkul`}
          description={item.summary}
          canonicalPath={`/karsilastirma/${item.slug}`}
        />
        <HeaderNavigation />

        <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
          <Breadcrumbs
            items={[
              { label: "Gayrimenkul Karşılaştırmaları", url: "/karsilastirma" },
              { label: item.title, url: `/karsilastirma/${item.slug}` }
            ]}
          />

          <article className="space-y-6 my-6">
            <div className="space-y-3">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 w-fit">
                <Scale className="h-3.5 w-3.5" /> Adana Karşılaştırma Rehberi
              </span>
              <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
                {item.title}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {item.summary}
              </p>
            </div>

            {/* Quick Answer Summary */}
            <p className="text-sm text-muted-foreground">Hazırlayan: Corner Gayrimenkul · İçerik kontrolü: <time dateTime={item.lastUpdated}>6 Ekim 2026</time></p>
            <QuickAnswerBox question={`${item.itemA} mı ${item.itemB} mi?`} answer={item.quickAnswer} sourceText="Corner Gayrimenkul editoryal karşılaştırma rehberi" />

            <section className="grid sm:grid-cols-2 gap-4" aria-label="Sitedeki yayımlanmış ilanlar">
              {[{ name: item.itemA, area: item.areaA }, { name: item.itemB, area: item.areaB }].map(({ name, area }) => <div key={name} className="rounded-2xl border border-border bg-muted/30 p-5"><h2 className="font-bold text-lg">{name}</h2><p className="text-3xl font-black text-primary mt-3">{loading || error ? "—" : countForArea(area)}</p><p className="text-xs text-muted-foreground mt-1">{loading ? "İlanlar yükleniyor" : error ? "İlan sayısı şu anda yüklenemiyor" : "Bu sitede şu anda yayımlanmış ilan"}</p><Button asChild variant="outline" size="sm" className="mt-4"><Link to={area.path}>Bölgeyi inceleyin <ArrowRight className="h-4 w-4 ml-2" /></Link></Button></div>)}
            </section>

            {/* Comparison Criteria Table */}
            <div className="bg-card rounded-2xl border border-border shadow-md overflow-hidden my-8">
              <div className="bg-muted p-4 border-b border-border flex items-center gap-2">
                <Table className="h-5 w-5 text-primary" />
                <h3 className="font-extrabold text-lg text-foreground">Aynı ölçütlerle karşılaştırın</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <caption className="sr-only">{item.itemA} ve {item.itemB} için mülk inceleme ölçütleri</caption>
                  <thead className="bg-muted/40 border-b border-border text-xs uppercase text-muted-foreground font-bold">
                    <tr>
                      <th scope="col" className="p-4">Kriter</th>
                      <th scope="col" className="p-4 text-primary font-bold">{item.itemA}</th>
                      <th scope="col" className="p-4 text-primary font-bold">{item.itemB}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {item.criteriaRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/20 transition-colors">
                        <td className="p-4 font-bold text-foreground">{row.criterion}</td>
                        <td className="p-4 text-muted-foreground">
                          {row.valueA}
                        </td>
                        <td className="p-4 text-muted-foreground">
                          {row.valueB}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 bg-muted/30 text-xs text-muted-foreground text-right border-t border-border">
                Editoryal kontrol listesi. Canlı ilan sayıları yalnızca bu sitedeki yayımlanmış portföyleri kapsar.
              </div>
            </div>

            {/* Expert Verdict */}
            <div className="bg-card p-6 rounded-2xl border border-border space-y-4">
              <h3 className="font-extrabold text-xl text-foreground">Kararınızı mülke göre verin</h3>
              <div className="space-y-3 text-muted-foreground text-base leading-relaxed">
                {item.expertVerdict.map((verdict, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <p>{verdict}</p>
                  </div>
                ))}
              </div>
            </div>
            <section className="rounded-2xl border border-border bg-muted/30 p-6"><h2 className="font-extrabold text-lg mb-4">Resmî kaynaklar</h2><div className="flex flex-col gap-3">{item.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex gap-2 items-center text-sm text-primary underline underline-offset-2">{source.title}<ExternalLink className="h-3.5 w-3.5 shrink-0" /></a>)}</div></section>
          </article>
        </main>

        <FooterSEO />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Adana Gayrimenkul Karşılaştırmaları | Seyhan vs Çukurova"
        description="Seyhan, Çukurova, Gürselpaşa ve Güzelyalı'daki mülkleri aynı ölçütlerle karşılaştırın. Kaynaklı kontrol listeleri ve sitedeki canlı ilan sayıları."
        canonicalPath="/karsilastirma"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Karşılaştırmalar" }]} />

        <div className="mb-12 text-center max-w-3xl mx-auto">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
            Detaylı Kıyaslama Tabloları
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight my-4">
            Adana Gayrimenkul Karşılaştırmaları
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            İlçe veya mahalle seçerken mülkün kendi özelliklerini inceleyin. Kaynaklı kontrol listeleriyle aynı ölçütleri kullanarak seçeneklerinizi karşılaştırın.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
          {COMPARISONS.map((comp) => (
            <Card key={comp.id} className="p-6 bg-card border border-border/80 hover:border-primary transition-all shadow-xs flex flex-col justify-between">
              <div>
                <h3 className="font-extrabold text-xl text-foreground mb-3 hover:text-primary transition-colors">
                  <Link to={`/karsilastirma/${comp.slug}`}>
                    {comp.title}
                  </Link>
                </h3>
                <QuickAnswerBox answer={comp.quickAnswer} sourceText="Corner Gayrimenkul editoryal karşılaştırma rehberi" />
              </div>

              <div className="pt-4 border-t border-border/60 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Son Güncelleme: {comp.lastUpdated}</span>
                <Link to={`/karsilastirma/${comp.slug}`} className="text-sm font-bold text-primary hover:underline flex items-center gap-1">
                  Karşılaştırmayı İncele <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
